import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SparklesIcon, ChatBubbleLeftRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { PMDialog } from './PMDialog';
import { PMField } from './PMField';
import { aiProviders } from '../../config/aiGeneratorConfigs';

export const AIGeneratorDialog = ({
  isOpen,
  onClose,
  onGenerate,
  module,
  context,
  fieldsToGenerate,
  config,
  loading
}) => {
  const { t } = useTranslation();
  const [selectedProvider, setSelectedProvider] = useState('chatgpt');
  const [options, setOptions] = useState(() => {
    const defaults = {};
    if (config?.options) {
      Object.keys(config.options).forEach(key => {
        defaults[key] = config.options[key].default;
      });
    }
    return defaults;
  });

  useEffect(() => {
    // Reset to defaults when dialog opens
    if (isOpen) {
      setSelectedProvider('chatgpt');
      const defaults = {};
      if (config?.options) {
        Object.keys(config.options).forEach(key => {
          defaults[key] = config.options[key].default;
        });
      }
      setOptions(defaults);
    }
  }, [isOpen, config]);

  const handleGenerate = () => {
    onGenerate({
      provider: selectedProvider,
      options
    });
  };

  const selectedProviderInfo = aiProviders[selectedProvider];

  return (
    <PMDialog
      open={isOpen}
      onClose={onClose}
      title={t("Generer innhold med AI")}
      size="lg"
    >
      <div className="space-y-6">
        {/* AI Provider Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t("Velg AI-leverandør")}
          </label>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(aiProviders).map(([key, provider]) => (
              <button
                key={key}
                onClick={() => setSelectedProvider(key)}
                className={`relative p-4 border-2 rounded-lg transition-all ${
                  selectedProvider === key
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    {key === 'grok' ? (
                      <SparklesIcon className={`h-6 w-6 ${provider.color} mt-0.5`} />
                    ) : (
                      <ChatBubbleLeftRightIcon className={`h-6 w-6 ${provider.color} mt-0.5`} />
                    )}
                    <div className="text-left">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">{provider.label}</span>
                        {provider.recommended && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            {t("Anbefalt")}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{provider.description}</p>
                      <p className={`text-xs font-medium mt-1 ${
                        provider.cost === 'Gratis' ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {provider.cost === 'Gratis' ? '✓ ' : '⚠ '}{provider.cost}
                      </p>
                    </div>
                  </div>
                  {selectedProvider === key && (
                    <div className="flex-shrink-0">
                      <div className="h-5 w-5 rounded-full bg-indigo-600 flex items-center justify-center">
                        <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
                {provider.warning && selectedProvider === key && (
                  <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                    <div className="flex items-start">
                      <InformationCircleIcon className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                      <span>{provider.warning}</span>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Context Preview */}
        {context && Object.keys(context).length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("Kontekst som sendes")}
            </label>
            <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
              {Object.entries(context).map(([key, value]) => {
                if (!value) return null;
                const fieldConfig = config?.contextFields?.[key];
                return (
                  <div key={key} className="flex">
                    <span className="font-medium text-gray-600 w-32 flex-shrink-0">
                      {fieldConfig?.label || key}:
                    </span>
                    <span className="text-gray-900 flex-1">{value}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Fields to Generate */}
        {fieldsToGenerate && fieldsToGenerate.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("Felter som genereres")}
            </label>
            <div className="space-y-2">
              {fieldsToGenerate.map((field) => {
                const fieldConfig = config?.generatableFields?.[field];
                return (
                  <div key={field} className="flex items-center p-2 bg-blue-50 rounded">
                    <SparklesIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <div>
                      <span className="font-medium text-gray-900">
                        {fieldConfig?.label || field}
                      </span>
                      {fieldConfig?.description && (
                        <p className="text-xs text-gray-600">{fieldConfig.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Options */}
        {config?.options && Object.keys(config.options).length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t("Innstillinger")}
            </label>
            <div className="space-y-4">
              {Object.entries(config.options).map(([key, optionConfig]) => (
                <div key={key}>
                  {optionConfig.type === 'select' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {optionConfig.label}
                      </label>
                      <select
                        value={options[key] || optionConfig.default}
                        onChange={(e) => setOptions(prev => ({ ...prev, [key]: e.target.value }))}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        {optionConfig.options.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <PMField
                      label={optionConfig.label}
                      value={options[key] || ""}
                      onChange={(e) => setOptions((prev) => ({ ...prev, [key]: e.target.value }))}
                      type={optionConfig.type || "text"}
                      multiline={!!optionConfig.multiline}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            {t("Avbryt")}
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t("Genererer...")}
              </>
            ) : (
              <>
                {selectedProvider === 'grok' ? (
                  <SparklesIcon className="h-4 w-4 mr-2" />
                ) : (
                  <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                )}
                {t("Generer")}
              </>
            )}
          </button>
        </div>

        {/* Provider Info */}
        <div className="text-xs text-gray-500 text-center pt-2 border-t border-gray-100">
          {t("Bruker")} {selectedProviderInfo.label}
          {selectedProviderInfo.cost !== 'Gratis' && (
            <span className="text-orange-600 ml-1">({t("koster penger")})</span>
          )}
        </div>
      </div>
    </PMDialog>
  );
};
